"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { AstroMascot } from "./AstroMascot";
import { 
  X, 
  ArrowRight,
  Rocket,
  Sparkles
} from "lucide-react";

interface WelcomeBackBannerProps {
  candidateName: string;
  currentStep: string;
  onContinue: () => void;
  onDismiss: () => void;
  className?: string;
}

export function WelcomeBackBanner({
  candidateName,
  currentStep,
  onContinue,
  onDismiss,
  className = ""
}: WelcomeBackBannerProps) {
  const [isVisible, setIsVisible] = React.useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(), 300);
  };

  const getStepName = (step: string) => {
    const stepMap: Record<string, string> = {
      'astro-otp': 'Identity Verification',
      'astro-bulk': 'Document Upload',
      'astro-review': 'AI Review',
      'astro-joiner-type': 'Profile Setup',
      'astro-adaptive': 'Personalized Path',
      'astro-final': 'Final Review'
    };
    return stepMap[step] || 'your onboarding';
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.95 }}
          className={`${className}`}
        >
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 shadow-lg overflow-hidden relative">
            {/* Background decorations */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-2 right-8 opacity-20">
                <Sparkles className="w-6 h-6 text-blue-500" />
              </div>
              <div className="absolute bottom-2 left-8 opacity-20">
                <Rocket className="w-5 h-5 text-purple-500" />
              </div>
            </div>

            <div className="relative z-10 p-4">
              <div className="flex items-center space-x-4">
                {/* Astro Mascot */}
                <motion.div
                  animate={{ 
                    y: [0, -5, 0],
                    rotate: [0, 2, -2, 0]
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="flex-shrink-0"
                >
                  <AstroMascot expression="excited" size="md" />
                </motion.div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-gray-900 mb-1">
                        Welcome back, {candidateName.split(' ')[0]}! 👋
                      </h3>
                      <p className="text-gray-700 text-sm mb-3">
                        Let's wrap things up, shall we? You were working on{' '}
                        <span className="text-blue-700">{getStepName(currentStep)}</span>.
                      </p>
                      
                      <div className="flex items-center space-x-3">
                        <Button
                          onClick={onContinue}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2"
                        >
                          <Rocket className="w-4 h-4 mr-2" />
                          Continue Journey
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDismiss}
                          className="text-gray-600 hover:text-gray-800 text-sm"
                        >
                          Maybe later
                        </Button>
                      </div>
                    </div>

                    {/* Close button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDismiss}
                      className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Animated border */}
            <motion.div
              className="absolute inset-0 border-2 border-blue-300 rounded-lg"
              animate={{ 
                boxShadow: [
                  "0 0 0 0 rgba(59, 130, 246, 0.3)",
                  "0 0 0 4px rgba(59, 130, 246, 0.1)",
                  "0 0 0 0 rgba(59, 130, 246, 0.3)"
                ]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}